import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
export default function Background_Attachment() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📌 CSS Background Attachment</h1>

      <p className="tutorial-intro">
        The <code>background-attachment</code> property specifies whether a
        background image scrolls with the page or stays fixed. It can create
        effects like <strong>parallax scrolling</strong>.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Syntax</h2>
        <CodeCopyBlock
          code={`div {
  background-image: url("image.jpg");
  background-attachment: scroll | fixed | local;
}`}
        />
      </div>

      {/* scroll (default) */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ scroll (Default)</h2>
        <p>
          The background image <strong>scrolls</strong> with the content when
          you scroll the page.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("image.jpg");
  background-attachment: scroll;
}`}
        />
        <div
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1506748686214-e9df14d4d9d0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=60')",
            backgroundAttachment: "scroll",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            height: "250px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            marginTop: "1rem",
            overflow: "auto",
            color: "#fff",
            padding: "1rem",
          }}
        >
          <p style={{ height: "400px" }}>
            Scroll inside me ➡ Background moves with text.
          </p>
        </div>
      </div>

      {/* fixed */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ fixed</h2>
        <p>
          The background image stays <strong>fixed in place</strong>, even if
          you scroll. This creates a parallax effect.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("image.jpg");
  background-attachment: fixed;
}`}
        />
        <div
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=60')",
            backgroundAttachment: "fixed",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            height: "250px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "#fff",
            padding: "1rem",
          }}
        >
          <p style={{ height: "400px" }}>
            Scroll the page ➡ Background stays fixed in place.
          </p>
        </div>
      </div>

      {/* local */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ local</h2>
        <p>
          The background scrolls with the element’s <strong>content</strong>,
          not the page. Useful for scrollable boxes.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("image.jpg");
  background-attachment: local;
}`}
        />
        <div
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1491553895911-0055eca6402d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=60')",
            backgroundAttachment: "local",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            height: "250px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            marginTop: "1rem",
            overflow: "auto",
            color: "#fff",
            padding: "1rem",
          }}
        >
          <p style={{ height: "400px" }}>
            Scroll inside me ➡ Background moves only with content.
          </p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Background attachment is often used with{" "}
          <code>background-position</code> and <code>background-size</code> to
          create beautiful layouts. Next, try{" "}
          <a href="/css/background-position" className="learn-more">
            CSS Background Position
          </a>{" "}
          to learn how to place images exactly where you want them.
        </p>
      </div>
    </div>
  );
}
